import Link from 'next/link';
import styled from 'styled-components';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import { isExternalLink } from 'utils/helpers';
import YoutubeEmbed from './youtube-embed';
import VimeoEmbed from './vimeo-embed';
import ContentAlarm from './content-alarm';
import MarkdownImage from './markdown-image';

type MarkdownRenderProps = {
  markdown?: MDXRemoteSerializeResult<Record<string, unknown>>;
};

const components = {
  Updated({ children }) {
    return <>{children}</>;
  },
  img({ src, alt, width, height }) {
    return <MarkdownImage src={src} alt={alt} width={width} height={height} />;
  },
  table({ children }) {
    return (
      <div className="table-scroll-wrapper">
        <table className="table">{children}</table>
      </div>
    );
  },
  a({ href, children }) {
    if (isExternalLink(href)) {
      return (
        <a href={href} rel="noopener noreferrer" target="_blank">
          {children}
          <span className="docon docon-navigate-external" />
        </a>
      );
    }
    return <Link href={href}>{children}</Link>;
  },
  YoutubeEmbed({ id }) {
    return <YoutubeEmbed id={id} />;
  },
  VimeoEmbed({ src }) {
    return <VimeoEmbed src={src} />;
  },
  Tip({ children, ...props }) {
    return (
      <ContentAlarm type="tip" indent={props?.indent || 0}>
        {children}
      </ContentAlarm>
    );
  },
  Warning({ children, ...props }) {
    return (
      <ContentAlarm type="warning" indent={props?.indent || 0}>
        {children}
      </ContentAlarm>
    );
  },
  Caution({ children, ...props }) {
    return (
      <ContentAlarm type="caution" indent={props?.indent || 0}>
        {children}
      </ContentAlarm>
    );
  },
  Important({ children, ...props }) {
    return (
      <ContentAlarm type="important" indent={props?.indent || 0}>
        {children}
      </ContentAlarm>
    );
  },
  Note({ children, ...props }) {
    return (
      <ContentAlarm type="note" indent={props?.indent || 0}>
        {children}
      </ContentAlarm>
    );
  },
};

export const MarkdownRender: React.FC<MarkdownRenderProps> = ({ markdown }) => {
  return (
    <StyledMarkdownRender>
      <MDXRemote {...markdown} components={components} />
    </StyledMarkdownRender>
  );
};

const StyledMarkdownRender = styled.div`
  img {
    border: 0;
  }

  img,
  video {
    max-width: 100%;
    height: auto;
  }

  img,
  audio,
  canvas,
  progress,
  video {
    display: inline-block;
    vertical-align: baseline;
  }

  audio:not([controls]) {
    display: none;
    height: 0;
  }

  svg:not(:root) {
    overflow: hidden;
  }

  td img {
    max-width: none;
  }

  & ul ul {
    margin: 0;
    margin-left: 20px;
  }

  & ul ul > li {
    list-style-type: circle;
  }

  & ul ul ul > li {
    list-style-type: square;
  }

  & ol ol {
    margin: 0;
    margin-left: 20px;
  }

  & ol ol > li {
    list-style-type: lower-alpha;
  }

  & ol ol ol > li {
    list-style-type: lower-roman;
  }

  h1:first-of-type {
    margin-top: -10px;
    margin-bottom: 0;
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.3;
    margin-bottom: 12px;
    margin-top: 10px;
  }

  h1,
  h2.likeAnH1 {
    line-height: 1.3;
    font-size: clamp(1.875rem, 22.1052631579px + 1.6447368421vw, 2.5rem);
    margin-top: 32px;
    margin-top: 0;
    word-wrap: break-word; // browser support fallback, because FF and IE
    word-break: break-word;
  }

  h2 {
    font-size: clamp(1.59375rem, 18.7894736842px + 1.3980263158vw, 2.125rem);
    margin-top: 32px;
  }

  h3 {
    font-size: clamp(1.3125rem, 15.4736842105px + 1.1513157895vw, 1.75rem);
    margin-top: 30px;
    margin-bottom: 18px;
  }

  h4 {
    font-size: clamp(1.125rem, 13.2631578947px + 0.9868421053vw, 1.5rem);
    margin-top: 36px;
    margin-bottom: 6px;
  }

  h5 {
    font-size: clamp(1.1rem, 15.7052631579px + 0.3947368421vw, 1.25rem);
    letter-spacing: 1px;
    margin-top: 36px;
    margin-bottom: 6px;
  }

  h6 {
    font-size: clamp(1.05rem, 15.8526315789px + 0.1973684211vw, 1.125rem);
    letter-spacing: 1px;
    margin-top: 36px;
    margin-bottom: 6px;
  }

  p {
    margin-top: 1rem;
    margin-bottom: 0;
    word-wrap: break-word;
  }

  blockquote {
    display: block;
    margin: 1.2em 0 2em;
    padding: 0 0.8em;
    border-width: 0 0 0 3px;
    border-style: solid;
    border-color: $border;
  }

  blockquote footer {
    font-size: 0.9em;
    color: $text-subtle;
  }

  ul.clean,
  ol.clean {
    margin: 0;
    padding: 0;
    margin-bottom: 16px;
  }

  ul,
  ol {
    margin: 16px 0;
    margin-left: 38px;
  }

  ul > li,
  ol > li {
    list-style: disc outside none;
  }

  ul > li > ul > li {
    list-style-type: circle;
  }

  ol > li {
    list-style-type: decimal;
  }

  $checklist-marker-size: 1em;
  $checklist-ul-spacing: 1rem;

  .image-wrapper {
    margin: 1rem 0;

    > span {
      box-shadow: 0 -2px 2px rgba(0, 0, 0, 0.05), 0 1px 1px rgba(0, 0, 0, 0.05), 0 2px 2px rgba(0, 0, 0, 0.05),
        0 4px 4px rgba(0, 0, 0, 0.05), 0 8px 8px rgba(0, 0, 0, 0.05);

      /* display: block !important; */
    }
  }

  .table-scroll-wrapper {
    margin: 1rem 0;
    overflow-y: hidden;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .table {
    table-layout: auto;
    width: 100%;
    font-size: 0.875rem;

    td,
    th {
      display: table-cell;
      word-wrap: break-word;
      padding: 0.75rem 1rem;
      line-height: 1.5;
      vertical-align: top;
      border-top: 1px solid var(--border);
      border-right: 0;
      border-left: 0;
      border-bottom: 0;
      border-style: solid;

      &:not([align]) {
        text-align: left;
      }

      > *:first-child {
        margin-top: 0;
      }
    }

    th {
      vertical-align: bottom;
      &:not([scope='row']) {
        border-top: 0;
        border-bottom: 1px;
      }
    }

    .alarm {
      font-size: 0.875rem;
    }
  }

  /**** checklist list ****/

  .checklist > ul {
    margin-left: $checklist-ul-spacing;

    > li {
      list-style-type: none;
      position: relative;
      margin-left: $checklist-marker-size;

      &:before {
        position: absolute;
        -left: -$checklist-marker-size * 1.5;
        font-family: 'docons';
        content: get-icon-code('check');
        display: inline-block;
        height: $checklist-marker-size;
        width: $checklist-marker-size;
        color: $success;
      }
    }
  }

  td > ul:first-child {
    margin-top: 0;
  }

  &[lang^='zh'],
  &[lang^='ja'],
  &[lang^='ko'] {
    th {
      // Prevents line breaks at each character in multibyte languages (happens only in Chrome)
      white-space: nowrap;
    }
  }

  nobr {
    white-space: normal;
  }
`;

export default MarkdownRender;
